import React, {useState} from 'react'

// Vite emits these checked-in files on the frontend origin in both dev and build.
// Never use a backend-provided URL as an image request or as a fallback.
const images = import.meta.glob<string>(
    '/src/assets/images/upsells/**/*.{webp,png,jpg,jpeg,svg}',
    {eager: true, query: '?url', import: 'default'},
)

export function resolveUpsellImage(src?: string | null): string | undefined {
    if (!src?.trim()) return undefined
    let pathname: string
    try {
        const url = new URL(src, 'https://local.invalid')
        if (!['http:', 'https:'].includes(url.protocol)) return undefined
        pathname = decodeURIComponent(url.pathname).toLowerCase()
    } catch {
        return undefined
    }

    const entries = Object.entries(images)
    const relativePath = pathname.split('/upsells/')[1]
    if (relativePath) {
        const match = entries.find(([path]) =>
            path.toLowerCase() === `/src/assets/images/upsells/${relativePath}`,
        )
        if (match) return match[1]
    }

    const filename = pathname.split('/').pop()
    if (!filename) return undefined
    // Storage directories can differ; accept only an unambiguous local filename.
    const matches = entries.filter(([path]) => path.split('/').pop()?.toLowerCase() === filename)
    return matches.length === 1 ? matches[0][1] : undefined
}

interface UpsellImageProps {
    src?: string | null
    alt: string
    className?: string
}

export function UpsellImage({src, alt, className = 'w-full h-full object-cover'}: UpsellImageProps) {
    const localSrc = resolveUpsellImage(src)
    const [failedSrc, setFailedSrc] = useState<string>()

    if (!localSrc || failedSrc === localSrc) {
        return <span role="img" aria-label={alt} className={`${className} inline-block bg-gray-200 dark:bg-gray-800`} />
    }

    return <img src={localSrc} alt={alt} className={className} onError={() => setFailedSrc(localSrc)} />
}
